import React, { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useIntersectionObserver from '/src/hooks/useIntersectionObserver';
import Measure from 'react-measure';
import { AppleIcon, DriverIcon, AndroidIcon, StarIcon } from '/src/images/home';

const REVIEW_ROWS = [[{
	titleKey: 'this-app-is-great',
	sourceIcon: AppleIcon,
	stars: 5,
	date: '12/06/2020',
	author: 'Kimmstuhh',
	contentKey: 'i-use-this-as-my-daily',
}, {
	titleKey: 'wow',
	sourceIcon: AppleIcon,
	stars: 5,
	date: '06/15/2020',
	author: 'Icedragon28',
	contentKey: 'i-have-been-spending-months-searching',
}, {
	titleKey: 'perfect-replacement',
	sourceIcon: AppleIcon,
	stars: 5,
	date: '06/04/2022',
	author: 'Tomas',
	contentKey: 'this-app-is-the-perfect-replacement',
}, {
	sourceIcon: AndroidIcon,
	stars: 5,
	date: '06/28/2022',
	author: 'Robby',
	contentKey: 'i-drive-for-uber',
}], [{
	titleKey: 'great-app',
	sourceIcon: AppleIcon,
	stars: 5,
	date: '10/18/2021',
	author: 'Roberto826',
	contentKey: 'the-driver-app-works-great',
}, {
	titleKey: 'one-of-the-best-apps',
	sourceIcon: AppleIcon,
	stars: 5,
	date: '01/30/2022',
	author: 'Glfr4life',
	contentKey: 'i-think-this-app-is-one-of-the-best',
}, {
	sourceIcon: DriverIcon,
	stars: 5,
	author: 'Greg',
	contentKey: 'by-far-the-most-useful',
}, {
	titleKey: 'absolutely-amazing',
	sourceIcon: AppleIcon,
	stars: 5,
	date: '12/26/2021',
	author: 'M4vvv',
	contentKey: 'i-downloaded-this-because-its-snowing',
}]];

const CARD_MARGIN = 32;
const SCROLL_TEMPO = 32;

const ReviewCard = forwardRef(({
	index,
	titleKey,
	sourceIcon,
	stars,
	date,
	author,
	contentKey,
}, ref) => {
	const { t } = useTranslation(null, { keyPrefix: 'home-page.reviews' });

	return (
		<div className="review-card" ref={ref}>
			<header>
				{!!titleKey ? (
					<div><h4>{t(titleKey)}</h4></div>
				) : (<div />)}

				<div><img src={sourceIcon} alt={t('user-review')} /></div>

				<div>
					{[...Array(stars)].map((_, i) => (
						<img
							key={`review-${index}-star-${i}`}
							className="review-star"
							src={StarIcon}
							alt={`${stars} ${t('stars')}`}
						/>
					))}
				</div>
				<div><span>{date}</span></div>
			</header>

			<article>
				<p className="semibold">{author}</p>
				<p>{t(contentKey)}</p>
			</article>
		</div>
	);
});
//eslint throws missing display name when using forwardRef on component;
ReviewCard.displayName = 'ReviewCard';

const HomeReviews = () => {
	const { t } = useTranslation(null, { keyPrefix: 'home-page.reviews' });

	const [observerRef, isVisible] = useIntersectionObserver({ threshold: 0.2 });

	const [containerPadding, setContainerPadding] = useState(0);
	const [reviewCardWidth, setReviewCardWidth] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);
	const [reviewCards, setReviewCards] = useState([]);

	const reviewContainer = useRef();

	const pauseAutoScroll = useCallback(() => {
		setIsPlaying(false);
	}, []);

	const resumeAutoScroll = useCallback(() => {
		setIsPlaying(true);
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.document) return;

		const containerStyles = window.getComputedStyle(reviewContainer.current);
		const padding = parseFloat(containerStyles?.paddingLeft);

		setContainerPadding(padding);
	}, []);

	useEffect(() => {
		setReviewCards([
			[...REVIEW_ROWS[0], REVIEW_ROWS[0][0]],
			[...REVIEW_ROWS[1], REVIEW_ROWS[1][0]],
		]);
	}, []);

	useEffect(() => {
		if (!reviewContainer.current) return;

		const autoPlay = setInterval(() => {
			const { scrollLeft } = reviewContainer.current;

			if (!isVisible || !isPlaying) return;

			if (scrollLeft >= reviewCardWidth + containerPadding) {
				setReviewCards([
					[...reviewCards[0].slice(1), reviewCards[0][1]],
					[...reviewCards[1].slice(1), reviewCards[1][1]],
				]);

				reviewContainer.current.scroll(containerPadding - CARD_MARGIN, 0);
			} else {
				reviewContainer.current.scroll(scrollLeft + 1, 0);
			}
		}, SCROLL_TEMPO);

		return () => {
			clearInterval(autoPlay);
		};
	}, [reviewCardWidth, isVisible, isPlaying, reviewCards, containerPadding]);

	const initCardSize = useCallback(({ bounds }) => {
		setReviewCardWidth(bounds?.width || 0);
	}, []);

	return (
		<section id="reviews" ref={observerRef}>
			<div>
				<h3>{t('the-reviews-are-in')}</h3>

				<div className="total-ratings semibold">
					<span><strong>{t('four-point-seven')}</strong> {t('out-of-five')}</span>
					<a href="https://apps.apple.com/us/app/driver-dash-cam-cloud-sync/id1415557883" target="_blank" rel="noreferrer">
						<span>{t('ten-k-ratings')}</span>
					</a>
				</div>

				<div
					className="review-cards-container"
					ref={reviewContainer}
					onMouseEnter={pauseAutoScroll}
					onMouseLeave={resumeAutoScroll}
					onTouchMove={pauseAutoScroll}
					onTouchEnd={resumeAutoScroll}>
					<Measure onResize={initCardSize} bounds>
						{({ measureRef }) => {
							return reviewCards.map((reviews, i) => (
								<div key={`reviews-row-${i}`} className="review-cards-row">
									{reviews.map((review, j) => (
										<ReviewCard
											ref={i === 0 && j === 0 ? measureRef : undefined}
											key={`review-${i}-${j}`}
											index={`${i}-${j}`}
											{...review}
										/>
									))}
								</div>
							));
						}}
					</Measure>
				</div>
			</div>
		</section>
	);
};

export default HomeReviews;
